import * as apiUser from 'api/endpoints/user/user.api';
import * as apiCompany from 'api/endpoints/company/company.api';
import * as UserTypes from './user.types';
import { notificationErr } from 'views/components/UI/notification';
import { userError } from 'utils/errorCodes';
import { messageError, messageSuccess } from 'views/components/UI/message';
import * as key from 'utils/constants';
import { TUpdateLanguageProps, TUpdateUserPick } from 'interfaces/user.interface';
import {
  IloginDbUserAPIResponseData,
  IUpdateUserAPIBody,
} from 'api/endpoints/user/user.response.interface';
import { ITranslate } from 'interfaces/general.interface';
import { LogoutOptions } from '@auth0/auth0-react';
import { TuserDispatchReducer } from './user.reducer';
import { IgetClientsAPIResponseDataItemUserItem } from 'api/endpoints/superAdmin/superAdmin.response.interface';

export async function loginUserAction({
  dispatch,
  token,
  emailVerified,
  translate,
  logout,
}: {
  dispatch: React.Dispatch<TuserDispatchReducer>;
  token: string;
  emailVerified: boolean;
  translate: ITranslate;
  logout: (options?: LogoutOptions) => Promise<void>;
}): Promise<boolean> {
  let user: IloginDbUserAPIResponseData | null = null;
  dispatch({ type: UserTypes.SET_IS_LOADING, payload: true });
  try {
    const userFetch = await apiUser.loginDbUserAPI({ token });

    if (userFetch?.response?.status === 404) {
      logout();
      return false;
    }

    if (userFetch?.response?.status === 403) {
      window.location.replace(getUrlToRedirectId() as string);
      return false;
    }

    if (userFetch.response?.status === 200) {
      user = userFetch.data;
      dispatch({
        type: UserTypes.LOGIN_USER,
        payload: userFetch.data,
      });
      return true;
    }
    if (userFetch.response?.status !== 503) {
      notificationErr(userError.login_user_001, translate);
    }
    return false;
  } catch (err) {
    notificationErr(userError.login_user_002, translate);
    return false;
  } finally {
    dispatch({ type: UserTypes.SET_IS_LOADING, payload: false });
    if (user && user.enabled === false && emailVerified === true) {
      try {
        const body: IUpdateUserAPIBody = {
          filter: { id: user.id },
          update: { enabled: true },
        };
        const verifyFetch = await apiUser.updateDbUserAPI({ body, token });
        if (verifyFetch.response.status === 200) {
          dispatch({
            type: UserTypes.VERIFY_EMAIL,
            payload: true,
          });
        }
      } catch (error) {
        messageError(translate('action_user_login-user-action_verify-error'));
        notificationErr(userError.login_user_003, translate);
      }
    }
  }
}

export async function updateUserAction({
  dispatch,
  dbUserId,
  user,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TuserDispatchReducer>;
  dbUserId: string;
  user: TUpdateUserPick;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  const body: IUpdateUserAPIBody = { filter: { id: dbUserId }, update: user };
  try {
    const fetch = await apiUser.updateDbUserAPI({ body, token });

    if (fetch.response.status === 403) {
      messageError(translate('action_user_update-user-action_error-1'));
      notificationErr(userError.update_user_002, translate);
      return false;
    }

    if (fetch.response.status === 200) {
      messageSuccess(translate('action_user_update-user-action_success'));
      dispatch({
        type: UserTypes.UPDATE_USER,
        payload: user,
      });
      return true;
    }
    return false;
  } catch (err) {
    messageError(translate('action_user_update-user-action_error-2'));
    notificationErr(userError.update_user_001, translate);
    return false;
  }
}

export async function updateUserAvatarAction({
  dispatch,
  userId,
  image,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TuserDispatchReducer>;
  userId: string;
  image: File;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const update = await apiUser.updateUserAvatarAPI({ userId, image, token });
    if (update.response.status === 200) {
      messageSuccess(translate('action_user_update-user-avatar-action_success'));
      dispatch({
        type: UserTypes.UPDATE_USER_AVATAR,
        payload: update.data,
      });
      return true;
    }
    messageError(translate('action_user_update-user-avatar-action_error-1'));
    notificationErr(userError.update_user_avatar_002, translate);
    return false;
  } catch (err) {
    messageError(translate('action_user_update-user-avatar-action_error-1'));
    notificationErr(userError.update_user_avatar_001, translate);
    return false;
  }
}

export async function updateLanguageAction({
  dispatch,
  dbUserId,
  language,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TuserDispatchReducer>;
  dbUserId: string;
  language: TUpdateLanguageProps;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  const body: IUpdateUserAPIBody = {
    filter: { id: dbUserId },
    update: { i18n_lang_id: language.id },
  };
  try {
    const updateLanguageFetch = await apiUser.updateDbUserAPI({ body, token });
    if (updateLanguageFetch.data.updated_user[0] === 1) {
      dispatch({ type: UserTypes.UPDATE_LANGUAGE, payload: language });
      return true;
    }
    notificationErr(userError.update_language_002, translate);
    return false;
  } catch (err) {
    notificationErr(userError.update_language_001, translate);
    return false;
  }
}

export async function selectUserSuperAdminAction({
  dispatch,
  user,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TuserDispatchReducer>;
  user: IgetClientsAPIResponseDataItemUserItem;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({ type: UserTypes.SET_IS_LOADING, payload: true });
  try {
    const companyFetch = await apiCompany.getCompanyInfoAPI({
      clientId: user.client_id,
      token,
    });
    if (companyFetch.response.status === 200) {
      dispatch({
        type: UserTypes.LOGIN_USER_SUPER_ADMIN,
        payload: user,
      });
      return true;
    }
    notificationErr(userError.selectUserSuperAdmin_002, translate);
    return false;
  } catch (err) {
    notificationErr(userError.selectUserSuperAdmin_001, translate);
    return false;
  } finally {
    dispatch({ type: UserTypes.SET_IS_LOADING, payload: false });
  }
}

/////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////

const getUrlToRedirectId = () => {
  if (
    process.env.REACT_APP_NODE_ENV === key.clientLocalKey ||
    !process.env.REACT_APP_NODE_ENV
  ) {
    return key.clientLocal;
  } else if (process.env.REACT_APP_NODE_ENV === key.clientProdKey) {
    return key.clientProd;
  } else if (process.env.REACT_APP_NODE_ENV === key.clientDevKey) {
    return key.clientDev;
  } else if (process.env.REACT_APP_NODE_ENV === key.clientStagingKey) {
    return key.clientStaging;
  }
};
